/**
 * A local log of recognition attempts, purely so a usage summary can be
 * produced on request — nothing here can watch Anthropic billing directly.
 * Cost estimates are opt-in and admin-supplied, never a hardcoded $/token
 * constant that could go stale.
 */

export type RecognitionOutcome = 'success' | 'insufficient_credit' | 'rate_limited' | 'other';

export interface UsageLogEntry {
  timestamp: number;
  imageCount: number;
  outcome: RecognitionOutcome;
  inputTokens?: number;
  outputTokens?: number;
}

export interface UsageLogSummary {
  totalCalls: number;
  totalImages: number;
  byOutcome: Record<RecognitionOutcome, number>;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface CostRates {
  inputPerMillionUsd: number;
  outputPerMillionUsd: number;
}

const LOG_KEY = 'mahjong-scorer:recognitionUsageLog';
const RATES_KEY = 'mahjong-scorer:recognitionCostRates';
const MAX_ENTRIES = 500;

export function readUsageLog(): UsageLogEntry[] {
  const stored = localStorage.getItem(LOG_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored) as UsageLogEntry[];
  } catch {
    return [];
  }
}

export function logRecognitionAttempt(entry: UsageLogEntry): void {
  const entries = [...readUsageLog(), entry].slice(-MAX_ENTRIES);
  localStorage.setItem(LOG_KEY, JSON.stringify(entries));
}

export function clearUsageLog(): void {
  localStorage.removeItem(LOG_KEY);
}

export function summariseUsageLog(entries: UsageLogEntry[]): UsageLogSummary {
  const byOutcome: Record<RecognitionOutcome, number> = {
    success: 0,
    insufficient_credit: 0,
    rate_limited: 0,
    other: 0,
  };
  let totalImages = 0;
  let totalInputTokens = 0;
  let totalOutputTokens = 0;

  for (const entry of entries) {
    byOutcome[entry.outcome]++;
    totalImages += entry.imageCount;
    totalInputTokens += entry.inputTokens ?? 0;
    totalOutputTokens += entry.outputTokens ?? 0;
  }

  return {
    totalCalls: entries.length,
    totalImages,
    byOutcome,
    totalInputTokens,
    totalOutputTokens,
  };
}

export function readCostRates(): CostRates | undefined {
  const stored = localStorage.getItem(RATES_KEY);
  if (!stored) return undefined;
  try {
    return JSON.parse(stored) as CostRates;
  } catch {
    return undefined;
  }
}

export function writeCostRates(rates: CostRates | undefined): void {
  if (rates) {
    localStorage.setItem(RATES_KEY, JSON.stringify(rates));
  } else {
    localStorage.removeItem(RATES_KEY);
  }
}

/** Rough estimate only — the caller is responsible for labeling it as such. */
export function estimateCostUsd(summary: UsageLogSummary, rates: CostRates): number {
  return (
    (summary.totalInputTokens / 1_000_000) * rates.inputPerMillionUsd +
    (summary.totalOutputTokens / 1_000_000) * rates.outputPerMillionUsd
  );
}
